import React, { useState } from "react";
import { Trophy, CheckCircle, Circle, CaretDown, CaretUp, Lightbulb } from "@phosphor-icons/react";

const PACE_STYLE = {
  Early: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  Advancing: "border-sky-400/40 bg-sky-400/10 text-sky-300",
  Veteran: "border-ff-gold/45 bg-ff-gold/10 text-ff-gold",
  "Near Mastery": "border-fuchsia-400/40 bg-fuchsia-400/10 text-fuchsia-300",
};

const STATUS_LABEL = {
  xp_pending: ["Earning XP", "text-ff-muted"],
  gate_pending: ["XP ready · gate to complete", "text-amber-300"],
  ready: ["Qualified to advance", "text-emerald-300"],
  maxed: ["Mastered", "text-ff-gold"],
};

export default function CrewProgression({ id, progression }) {
  const [showAll, setShowAll] = useState(false);
  if (!progression) return null;
  const p = progression;
  const pct = p.maxed ? 100 : Math.min(100, Math.round((p.xp / Math.max(1, p.xp_required)) * 100));
  const [statusText, statusColor] = STATUS_LABEL[p.status] || STATUS_LABEL.xp_pending;
  const paceCls = PACE_STYLE[p.pace] || PACE_STYLE.Early;

  return (
    <div className="mt-3 rounded-sm border border-ff-brass/20 bg-ff-bg/40 p-3" data-testid={`crew-progression-${id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-ff-parchment">
          <Trophy size={16} weight="duotone" className="text-ff-gold" /> Level {p.level}
        </div>
        <span
          className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${paceCls}`}
          data-testid={`crew-pace-${id}`}
        >
          {p.pace}
        </span>
      </div>

      {p.maxed ? (
        <p className="mt-2 text-xs text-ff-gold" data-testid={`crew-hint-${id}`}>{p.next_hint}</p>
      ) : (
        <>
          <div className="mt-2 flex items-baseline justify-between text-[11px]">
            <span className="text-ff-muted">Level {p.target_level} progress</span>
            <span className="font-mono-ff text-ff-parchment" data-testid={`crew-xp-${id}`}>
              {p.xp.toLocaleString()} / {p.xp_required.toLocaleString()} XP
            </span>
          </div>
          <div
            className="mt-1 h-2 w-full overflow-hidden rounded-full bg-ff-brass/15"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Level ${p.target_level} experience progress: ${pct} percent`}
          >
            <div
              className="h-full rounded-full bg-ff-gold transition-[width] duration-500 ease-out motion-reduce:transition-none"
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className={`mt-2 text-[11px] font-medium ${statusColor}`} data-testid={`crew-status-${id}`}>
            {statusText}
          </div>

          {p.gated && (
            <div className="mt-2 space-y-1" data-testid={`crew-gates-${id}`}>
              {/* Show the completed gates and, when qualified, all met; otherwise the top actionable remaining one. */}
              {(showAll ? p.gates : p.gates.filter((g) => g.met).concat(p.gates.filter((g) => !g.met).slice(0, 1))).map((g, i) => (
                <div key={g.key} className="flex items-start gap-1.5 text-[11px]" data-testid={`crew-gate-${id}-${g.key}`}>
                  {g.met ? (
                    <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-emerald-400" />
                  ) : (
                    <Circle size={14} className="mt-0.5 shrink-0 text-ff-muted" />
                  )}
                  <span className={g.met ? "text-ff-muted line-through" : "text-ff-parchment"}>
                    {g.label}
                    {!g.met && g.key !== "loyalty" && (
                      <span className="ml-1 font-mono-ff text-ff-muted">({g.current}/{g.target})</span>
                    )}
                  </span>
                </div>
              ))}
              {p.gates.filter((g) => !g.met).length > 1 && (
                <button
                  onClick={() => setShowAll((v) => !v)}
                  data-testid={`crew-gates-toggle-${id}`}
                  className="flex items-center gap-1 text-[11px] text-ff-brass transition-colors hover:text-ff-gold"
                >
                  {showAll ? <><CaretUp size={12} /> Fewer requirements</> : <><CaretDown size={12} /> View all requirements</>}
                </button>
              )}
            </div>
          )}

          <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-ff-brass" data-testid={`crew-hint-${id}`}>
            <Lightbulb size={13} weight="duotone" className="mt-0.5 shrink-0" /> {p.next_hint}
          </p>
        </>
      )}
    </div>
  );
}
